import { supabase } from '@/lib/supabase'
import { env } from '@/env'
import type { CanonicalEventRow, ImportPlan } from '@/lib/eventbrite'
import type { ImportSummary } from '@/types/database'

/**
 * Client for the `eventbrite-import` Edge Function.
 *
 * The browser never sees the Eventbrite token — it asks the function to do the
 * fetch. It does send the admin's Supabase JWT, which is what the database uses
 * to decide whether the import is allowed.
 */

export interface ImportRequest {
  eventId: string
  eventbriteEventId: string
  /** Preview the diff without writing anything. */
  dryRun?: boolean
  /** Mark previously imported attendees who are absent as cancelled. */
  cancelMissing?: boolean
  /** Eventbrite custom-question labels, when those fields are not on the profile. */
  genderQuestion?: string
  phoneQuestion?: string
}

/** Which Eventbrite credentials a request used. */
export type CredentialSource = 'organization' | 'deployment' | 'stub'

interface BaseResponse {
  /** False when the function fell back to the stub because no token is set. */
  live_eventbrite: boolean
  /**
   * 'deployment' means this organization has not connected its own account and
   * is reading a shared one. Worth surfacing rather than hiding.
   */
  credential_source: CredentialSource
  /** Records Eventbrite returned that had no usable name. */
  skipped_rows: number
}

export interface ImportPreview extends BaseResponse {
  dryRun: true
  plan: ImportPlan
}

export interface ImportResult extends BaseResponse {
  dryRun: false
  result: ImportSummary
}

export class ImportError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, status: number, message?: string) {
    super(message ?? code)
    this.name = 'ImportError'
    this.code = code
    this.status = status
  }
}

const MESSAGES: Record<string, string> = {
  unauthorized: 'Your session has expired. Sign in again.',
  forbidden: 'You are not a member of that organization.',
  invalid_organization_id: 'That organization could not be found.',
  eventbrite_not_connected:
    'This organization has not connected an Eventbrite account yet. Connect one in Settings.',
  invalid_api_token: 'Enter your Eventbrite private token.',
  no_eventbrite_organizations: 'That token does not have access to any Eventbrite organization.',
  eventbrite_organization_not_accessible:
    'That token cannot reach that Eventbrite organization ID. Check both, or leave the ID blank to detect it automatically.',
  connect_failed: 'The Eventbrite account could not be connected.',
  invalid_event_id: 'That event could not be found.',
  invalid_eventbrite_event_id: 'Enter the Eventbrite event ID.',
  import_failed: 'The import could not be completed.',
  database_error: 'Could not read the current attendee list.',
  eventbrite_error: 'Eventbrite could not be reached. Check the API token.',
  method_not_allowed: 'Something went wrong. Please try again.',
}

export function importErrorMessage(code: string): string {
  return MESSAGES[code] ?? 'Something went wrong. Please try again.'
}

/** Shared transport for every call to the eventbrite-import function. */
async function callFunction(payload: Record<string, unknown>): Promise<unknown> {
  const { data: sessionData } = await supabase.auth.getSession()
  const accessToken = sessionData.session?.access_token

  if (!accessToken) {
    throw new ImportError('unauthorized', 401, MESSAGES.unauthorized)
  }

  let response: Response
  try {
    response = await fetch(`${env.supabaseUrl}/functions/v1/eventbrite-import`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: env.supabasePublishableKey,
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    })
  } catch {
    throw new ImportError('eventbrite_error', 0, 'Network request failed')
  }

  const body: unknown = await response.json().catch(() => null)

  if (!response.ok || (body as { ok?: boolean } | null)?.ok !== true) {
    const code = (body as { error?: string } | null)?.error ?? 'import_failed'
    const detail = (body as { detail?: string } | null)?.detail
    throw new ImportError(code, response.status, detail ?? importErrorMessage(code))
  }

  return body
}

export interface EventbriteEventList {
  events: CanonicalEventRow[]
  live_eventbrite: boolean
  credential_source: CredentialSource
  skipped_rows: number
}

export interface EventbriteOrganizationChoice {
  id: string
  name: string | null
}

export type ConnectResult =
  | { needsChoice: true; organizations: EventbriteOrganizationChoice[] }
  | {
      needsChoice: false
      eventbriteOrganizationId: string
      eventbriteOrganizationName: string | null
    }

/**
 * Validates an Eventbrite token and stores it for the organization.
 *
 * The token is checked against Eventbrite before it is saved, so a mistyped one
 * fails here with a clear message rather than as a broken import later. It goes
 * straight to the Edge Function and is written to a table no admin can read —
 * including the owner who set it.
 *
 * When the token can act for several Eventbrite organizations and none was
 * chosen, this returns the list instead of guessing.
 */
export async function connectEventbrite(
  organizationId: string,
  apiToken: string,
  eventbriteOrganizationId?: string
): Promise<ConnectResult> {
  const body = (await callFunction({
    action: 'connect_eventbrite',
    organization_id: organizationId,
    api_token: apiToken,
    ...(eventbriteOrganizationId === undefined
      ? {}
      : { eventbrite_organization_id: eventbriteOrganizationId }),
  })) as {
    needs_choice: boolean
    organizations?: EventbriteOrganizationChoice[]
    eventbrite_organization_id?: string
    eventbrite_organization_name?: string | null
  }

  if (body.needs_choice) {
    return { needsChoice: true, organizations: body.organizations ?? [] }
  }

  return {
    needsChoice: false,
    eventbriteOrganizationId: body.eventbrite_organization_id ?? '',
    eventbriteOrganizationName: body.eventbrite_organization_name ?? null,
  }
}

/**
 * Lists the Eventbrite events available to import.
 *
 * Which Eventbrite account is read is fixed by the function's configuration,
 * not by this call — the API token is deployment-wide, so letting the browser
 * choose would let any admin enumerate any account the token can reach.
 */
export async function listEventbriteEvents(organizationId: string): Promise<EventbriteEventList> {
  const body = (await callFunction({
    action: 'list_events',
    organization_id: organizationId,
  })) as EventbriteEventList

  return {
    events: body.events,
    live_eventbrite: body.live_eventbrite,
    credential_source: body.credential_source,
    skipped_rows: body.skipped_rows,
  }
}

async function callImport(request: ImportRequest, dryRun: boolean): Promise<unknown> {
  return await callFunction({
    event_id: request.eventId,
    eventbrite_event_id: request.eventbriteEventId,
    dry_run: dryRun,
    cancel_missing: request.cancelMissing ?? true,
    ...(request.genderQuestion === undefined ? {} : { gender_question: request.genderQuestion }),
    ...(request.phoneQuestion === undefined ? {} : { phone_question: request.phoneQuestion }),
  })
}

/** Shows the admin what a re-import would change before it changes it. */
export async function previewImport(request: ImportRequest): Promise<ImportPreview> {
  const body = (await callImport(request, true)) as {
    plan: ImportPlan
    live_eventbrite: boolean
    credential_source: CredentialSource
    skipped_rows: number
  }

  return {
    dryRun: true,
    plan: body.plan,
    live_eventbrite: body.live_eventbrite,
    credential_source: body.credential_source,
    skipped_rows: body.skipped_rows,
  }
}

export async function runImport(request: ImportRequest): Promise<ImportResult> {
  const body = (await callImport(request, false)) as {
    result: ImportSummary
    live_eventbrite: boolean
    credential_source: CredentialSource
    skipped_rows: number
  }

  return {
    dryRun: false,
    result: body.result,
    live_eventbrite: body.live_eventbrite,
    credential_source: body.credential_source,
    skipped_rows: body.skipped_rows,
  }
}
